/**
 * EmployeeLifecycle — top-level lifecycle page.
 *
 *   - Onboarding  → <OnboardingBoard />
 *   - Offboarding → <OffboardingBoard /> (case-engine cockpit)
 *   - Work Pass   → <WorkPassBoard />
 *   - Termination Requests → <TerminationRequestBoard />
 *
 * Path-style deep links:
 *   /admin/employee-lifecycle/onboarding/:publicRef
 *   /admin/employee-lifecycle/offboarding/:workflowId
 * Query deep links:
 *   /admin/employee-lifecycle?tab=work-pass&staff_id=
 *   /admin/employee-lifecycle?tab=termination-requests&id=
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UserPlus, UserMinus, Shield, LogOut } from 'lucide-react';
import WorkPassBoard from './WorkPassBoard';
import TerminationRequestBoard from './TerminationRequestBoard';
import { useHRWorkPassDecisions } from '../../hooks/useWorkPassDecisions';
import { useHRTerminationRequests } from '../../hooks/useTerminationRequests';
import { CountBadge } from '../../components/UI/CountBadge';
import { useTabPersistence } from '../../hooks/usePersistence';
import { useOffboardingCases } from '../../hooks/useOffboardingCases';
import OnboardingBoard from './OnboardingBoard';
import OffboardingBoard from './OffboardingBoard';

const EmployeeLifecycle = () => {
  const { publicRef, workflowId } = useParams();
  const validTabs = useMemo(
    () => ['onboarding', 'offboarding', 'work-pass', 'termination-requests'],
    [],
  );
  const pathIsOffboarding = Boolean(workflowId);
  const [onboardingActiveCount, setOnboardingActiveCount] = useState(0);

  const [activeTab, setActiveTab] = useTabPersistence(
    'employee-lifecycle',
    pathIsOffboarding ? 'offboarding' : 'onboarding',
    validTabs
  );

  const { data: cases = [] } = useOffboardingCases({ status: 'active' });
  const { data: workPassDecisions = [] } = useHRWorkPassDecisions();
  const { data: terminationRequests = [] } = useHRTerminationRequests();
  const activeCount = cases.filter((item) => (
    item.workflow_status === 'initiated' || item.workflow_status === 'in_progress'
  )).length;
  const openWorkPassCount = workPassDecisions.filter((item) => (
    !['completed', 'superseded', 'cancelled'].includes(item.status)
  )).length;
  const openTerminationCount = terminationRequests.filter((item) => (
    ['awaiting_worker_acknowledgement', 'awaiting_department_acknowledgement', 'returned_for_correction', 'disputed', 'pending_hr_review'].includes(item.status)
  )).length;

  useEffect(() => {
    if (publicRef && activeTab !== 'onboarding') {
      setActiveTab('onboarding');
    }
    if (workflowId && activeTab !== 'offboarding') {
      setActiveTab('offboarding');
    }
  }, [publicRef, workflowId, activeTab, setActiveTab]);

  const isWorklistTab = activeTab === 'work-pass' || activeTab === 'termination-requests';

  return (
    <div
      className={
        isWorklistTab
          ? 'bg-gray-50 flex flex-col gap-6 lg:h-[calc(100dvh-7.5rem)] lg:min-h-0 lg:overflow-hidden'
          : 'bg-gray-50 space-y-6'
      }
    >
      <div className="min-w-0 flex-shrink-0">
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">
            Employee Lifecycle Management
          </h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
          Manage onboarding, offboarding, work-pass decisions, and termination requests
        </p>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth flex-shrink-0">
        <button
          type="button"
          onClick={() => setActiveTab('onboarding')}
          className={`tab-btn-admin ${activeTab === 'onboarding' ? 'active' : ''}`}
        >
          <UserPlus className="w-4 h-4" />
          Onboarding
          {onboardingActiveCount > 0 && (
            <CountBadge
              count={onboardingActiveCount}
              theme="admin"
              active={activeTab === 'onboarding'}
            />
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('offboarding')}
          className={`tab-btn-admin ${activeTab === 'offboarding' ? 'active' : ''}`}
        >
          <UserMinus className="w-4 h-4" />
          Offboarding
          {activeCount > 0 && (
            <CountBadge count={activeCount} theme="admin" active={activeTab === 'offboarding'} />
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('work-pass')}
          className={`tab-btn-admin ${activeTab === 'work-pass' ? 'active' : ''}`}
        >
          <Shield className="w-4 h-4" />
          Work Pass
          {openWorkPassCount > 0 && (
            <CountBadge count={openWorkPassCount} theme="admin" active={activeTab === 'work-pass'} />
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('termination-requests')}
          className={`tab-btn-admin ${activeTab === 'termination-requests' ? 'active' : ''}`}
        >
          <LogOut className="w-4 h-4" />
          Termination Requests
          {openTerminationCount > 0 && (
            <CountBadge count={openTerminationCount} theme="admin" active={activeTab === 'termination-requests'} />
          )}
        </button>
      </div>

      {activeTab === 'onboarding' && (
        <OnboardingBoard
          initialPublicRef={publicRef}
          onActiveCountChange={setOnboardingActiveCount}
        />
      )}
      {activeTab === 'offboarding' && (
        <OffboardingBoard initialCaseId={workflowId} />
      )}
      {activeTab === 'work-pass' && <WorkPassBoard />}
      {activeTab === 'termination-requests' && <TerminationRequestBoard />}
    </div>
  );
};

export default EmployeeLifecycle;
